import React, { useState, useEffect } from "react";
import {
    Card,
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    Table,
    Modal,
    message,
    Popconfirm,
    Space,
    Typography,
    Divider,
    Upload,
    Image,
    Progress,
    Alert,
    Tag,
    Row,
    Col,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SaveOutlined,
    CloseOutlined,
    UploadOutlined,
    PictureOutlined,
    EyeOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Cookies from "universal-cookie";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ExamManagement = ({ courseId }) => {
    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const config = { headers: { "auth-token": auth, "Content-Type": "application/json" } };

    // States
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [form] = Form.useForm();
    const [choices, setChoices] = useState(["", ""]); // เก็บเป็น array ของ string
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentQuestionImage, setCurrentQuestionImage] = useState(null);

    // Load questions on component mount
    useEffect(() => {
        if (courseId) {
            fetchQuestions();
        }
    }, [courseId]);

    // Fetch questions from API
    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/courses/${courseId}/questions`, config);
            setQuestions(response.data);
        } catch (error) {
            message.error("Failed to load questions");
            console.error("Error fetching questions:", error);
        } finally {
            setLoading(false);
        }
    };

    // Add new choice
    const addChoice = () => {
        setChoices([...choices, ""]);
    };

    // Remove choice
    const removeChoice = (index) => {
        if (choices.length > 2) {
            // ต้องมีอย่างน้อย 2 ตัวเลือก
            const newChoices = choices.filter((_, i) => i !== index);
            setChoices(newChoices);
        }
    };

    // Update choice
    const updateChoice = (index, value) => {
        const newChoices = [...choices];
        newChoices[index] = value;
        setChoices(newChoices);
    };

    // Image upload functions
    const beforeUpload = (file) => {
        const isImage = file.type.startsWith("image/");
        if (!isImage) {
            message.error("คุณสามารถอัพโหลดไฟล์รูปภาพเท่านั้น!");
            return false;
        }

        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error("ขนาดไฟล์ต้องไม่เกิน 5MB!");
            return false;
        }

        return true;
    };

    const handleImageUpload = async (file, questionId) => {
        if (!beforeUpload(file)) {
            return false;
        }

        setUploadingImage(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append("image", file);

        try {
            const response = await axios.post(`/api/courses/upload-question-image/${questionId}`, formData, {
                headers: {
                    "auth-token": auth,
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                },
            });

            if (response.data.success) {
                message.success("อัพโหลดรูปภาพเรียบร้อยแล้ว");
                setCurrentQuestionImage(response.data.imagePath);
                fetchQuestions(); // Refresh questions list
                return response.data.imagePath;
            }
        } catch (error) {
            console.error("Upload error:", error);
            message.error(error.response?.data?.message || "เกิดข้อผิดพลาดในการอัพโหลดรูปภาพ");
        } finally {
            setUploadingImage(false);
            setUploadProgress(0);
        }
        return false;
    };

    const handleImageDelete = async (questionId) => {
        try {
            const response = await axios.delete(`/api/courses/delete-question-image/${questionId}`, config);

            if (response.data.success) {
                message.success("ลบรูปภาพเรียบร้อยแล้ว");
                setCurrentQuestionImage(null);
                fetchQuestions(); // Refresh questions list
            }
        } catch (error) {
            console.error("Delete error:", error);
            message.error(error.response?.data?.message || "เกิดข้อผิดพลาดในการลบรูปภาพ");
        }
    };

    // Open modal for add/edit
    const openModal = (question = null) => {
        setEditingQuestion(question);
        setModalVisible(true);

        if (question) {
            // Edit mode
            form.setFieldsValue({
                question_text: question.question_text,
                explain_answer: question.explain_answer,
                points: question.points,
            });

            // แปลง choices object เป็น array ของ string
            const choiceTexts = question.choices?.map((choice) => choice.choice_text) || ["", ""];
            setChoices(choiceTexts.length >= 2 ? choiceTexts : ["", ""]);
            setCurrentQuestionImage(question.image_path);
        } else {
            // Add mode
            form.resetFields();
            setChoices(["", ""]); // เริ่มต้นด้วย 2 ตัวเลือกเปล่า
            setCurrentQuestionImage(null);
        }
    };

    // Close modal
    const closeModal = () => {
        setModalVisible(false);
        setEditingQuestion(null);
        form.resetFields();
        setChoices(["", ""]);
        setCurrentQuestionImage(null);
    };

    // Save question
    const saveQuestion = async () => {
        try {
            const values = await form.validateFields();
            values.points = 1;

            // Validate choices
            const validChoices = choices.filter((choice) => choice.trim() !== "");
            if (validChoices.length < 2) {
                message.error("กรุณาเพิ่มตัวเลือกอย่างน้อย 2 ตัวเลือก");
                return;
            }

            if (validChoices[0].trim() === "") {
                message.error("กรุณากรอกคำตอบที่ถูกต้อง (ตัวเลือกแรก)");
                return;
            }

            // สร้าง choices array โดยตัวเลือกแรกเป็นคำตอบที่ถูก
            const formattedChoices = validChoices.map((choice, index) => ({
                choice_text: choice.trim(),
                is_correct: index === 0, // ตัวเลือกแรกเป็นคำตอบที่ถูกเสมอ
            }));

            const questionData = {
                ...values,
                course_id: courseId,
                choices: formattedChoices,
                image_path: currentQuestionImage,
            };

            if (editingQuestion) {
                // Update existing question
                await axios.put(`/api/courses/questions/${editingQuestion.id}`, questionData, config);
                message.success("แก้ไขคำถามเรียบร้อยแล้ว");
            } else {
                // Create new question
                await axios.post(`/api/courses/${courseId}/questions`, questionData, config);
                message.success("เพิ่มคำถามเรียบร้อยแล้ว");
            }

            closeModal();
            fetchQuestions();
        } catch (error) {
            console.error("Save error:", error);
            message.error("เกิดข้อผิดพลาดในการบันทึกคำถาม");
        }
    };

    // Delete question
    const deleteQuestion = async (questionId) => {
        try {
            await axios.delete(`/api/courses/questions/${questionId}`, config);
            message.success("ลบคำถามเรียบร้อยแล้ว");
            fetchQuestions();
        } catch (error) {
            console.error("Delete error:", error);
            message.error("เกิดข้อผิดพลาดในการลบคำถาม");
        }
    };

    // Table columns
    const columns = [
        {
            title: "#",
            key: "index",
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: "คำถาม",
            dataIndex: "question_text",
            key: "question_text",
            ellipsis: true,
            render: (text, record) => (
                <div style={{ maxWidth: 350 }}>
                    {record.image_path && (
                        <div style={{ marginBottom: 8 }}>
                            <Tag icon={<PictureOutlined />} color="blue">
                                มีรูปภาพ
                            </Tag>
                        </div>
                    )}
                    <Text>{text}</Text>
                </div>
            ),
        },
        // {
        //     title: "คะแนน",
        //     dataIndex: "points",
        //     key: "points",
        //     width: 80,
        //     align: "center",
        // },
        {
            title: "ตัวเลือก",
            key: "choices",
            width: 100,
            align: "center",
            render: (_, record) => <Text>{record.choices?.length || 0} ตัวเลือก</Text>,
        },
        {
            title: "คำตอบที่ถูก",
            key: "correct_answer",
            width: 200,
            ellipsis: true,
            render: (_, record) => {
                const correctChoice = record.choices?.find((choice) => choice.is_correct);
                return <Text type="success">{correctChoice?.choice_text || "ไม่พบคำตอบ"}</Text>;
            },
        },
        {
            title: "การจัดการ",
            key: "actions",
            width: 150,
            align: "center",
            render: (_, record) => (
                <Space>
                    <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>
                        แก้ไข
                    </Button>
                    <Popconfirm
                        title="ยืนยันการลบ"
                        description="คุณแน่ใจหรือว่าต้องการลบคำถามนี้?"
                        onConfirm={() => deleteQuestion(record.id)}
                        okText="ยืนยัน"
                        cancelText="ยกเลิก"
                    >
                        <Button danger size="small" icon={<DeleteOutlined />}>
                            ลบ
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card>
                <div
                    style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <Title level={4}>จัดการข้อสอบ</Title>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
                        เพิ่มคำถาม
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={questions}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} จาก ${total} รายการ`,
                    }}
                />
            </Card>

            {/* Add/Edit Question Modal */}
            <Modal
                title={editingQuestion ? "แก้ไขคำถาม" : "เพิ่มคำถาม"}
                open={modalVisible}
                onCancel={closeModal}
                width={800}
                footer={[
                    <Button key="cancel" onClick={closeModal} icon={<CloseOutlined />}>
                        ยกเลิก
                    </Button>,
                    <Button key="save" type="primary" onClick={saveQuestion} icon={<SaveOutlined />}>
                        บันทึก
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="question_text"
                        label="คำถาม"
                        rules={[{ required: true, message: "กรุณากรอกคำถาม" }]}
                    >
                        <TextArea rows={4} placeholder="กรอกคำถาม" />
                    </Form.Item>

                    {/* Image Upload Section */}
                    <Form.Item label="รูปภาพประกอบคำถาม">
                        <Card size="small" style={{ backgroundColor: "#fafafa" }}>
                            {currentQuestionImage && (
                                <div style={{ textAlign: "center", marginBottom: 16 }}>
                                    <Image
                                        src={currentQuestionImage}
                                        alt="Question Image"
                                        style={{
                                            maxWidth: "200px",
                                            border: "1px solid #d9d9d9",
                                            borderRadius: "6px",
                                        }}
                                        preview={{
                                            mask: (
                                                <div style={{ color: "white" }}>
                                                    <EyeOutlined /> ดูรูปภาพ
                                                </div>
                                            ),
                                        }}
                                    />
                                </div>
                            )}

                            {uploadingImage && (
                                <Progress
                                    percent={uploadProgress}
                                    status="active"
                                    strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }}
                                    style={{ marginBottom: 16 }}
                                />
                            )}

                            <Space>
                                <Upload
                                    beforeUpload={(file) => {
                                        if (editingQuestion?.id) {
                                            handleImageUpload(file, editingQuestion.id);
                                        } else {
                                            message.warning("กรุณาบันทึกคำถามก่อนอัพโหลดรูปภาพ");
                                        }
                                        return false;
                                    }}
                                    showUploadList={false}
                                    accept="image/*"
                                    disabled={!editingQuestion?.id || uploadingImage}
                                >
                                    <Button
                                        icon={uploadingImage ? <LoadingOutlined /> : <UploadOutlined />}
                                        loading={uploadingImage}
                                        disabled={!editingQuestion?.id}
                                    >
                                        {currentQuestionImage ? "เปลี่ยนรูปภาพ" : "อัพโหลดรูปภาพ"}
                                    </Button>
                                </Upload>

                                {currentQuestionImage && editingQuestion?.id && (
                                    <Popconfirm
                                        title="ยืนยันการลบรูปภาพ"
                                        description="คุณแน่ใจหรือว่าต้องการลบรูปภาพนี้?"
                                        onConfirm={() => handleImageDelete(editingQuestion.id)}
                                        okText="ยืนยัน"
                                        cancelText="ยกเลิก"
                                    >
                                        <Button danger icon={<DeleteOutlined />}>
                                            ลบรูปภาพ
                                        </Button>
                                    </Popconfirm>
                                )}
                            </Space>

                            {!editingQuestion?.id && (
                                <Alert
                                    message="หมายเหตุ"
                                    description="กรุณาบันทึกคำถามก่อน จึงจะสามารถอัพโหลดรูปภาพได้"
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 8 }}
                                />
                            )}

                            <Alert
                                message="คำแนะนำการอัพโหลดรูปภาพ"
                                description={
                                    <ul style={{ marginBottom: 0, paddingLeft: 20, fontSize: "12px" }}>
                                        <li>รองรับไฟล์: JPG, PNG, GIF, WebP</li>
                                        <li>ขนาดไฟล์ไม่เกิน 5 MB</li>
                                        <li>ขนาดแนะนำ: 800x600 พิกเซล</li>
                                    </ul>
                                }
                                type="info"
                                showIcon
                                style={{ marginTop: 8, fontSize: "12px" }}
                            />
                        </Card>
                    </Form.Item>

                    {/* <Form.Item name="points" label="คะแนน" rules={[{ required: true, message: "กรุณากรอกคะแนน" }]}>
                        <InputNumber min={1} style={{ width: "100%" }} placeholder="กรอกคะแนน" />
                    </Form.Item> */}

                    <Divider>ตัวเลือกคำตอบ</Divider>

                    <Alert
                        message="หมายเหตุสำคัญ"
                        description="ตัวเลือกแรกจะเป็นคำตอบที่ถูกต้องเสมอ กรุณากรอกคำตอบที่ถูกต้องในช่องแรก"
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    {choices.map((choice, index) => (
                        <Row key={index} gutter={8} style={{ marginBottom: 8 }}>
                            <Col span={2}>
                                <div
                                    style={{
                                        padding: "6px 0",
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        color: index === 0 ? "#52c41a" : "#666",
                                    }}
                                >
                                    {index === 0 ? "✓" : index + 1}
                                </div>
                            </Col>
                            <Col span={18}>
                                <Input
                                    placeholder={
                                        index === 0 ? "คำตอบที่ถูกต้อง (ตัวเลือกแรก)" : `ตัวเลือกที่ ${index + 1}`
                                    }
                                    value={choice}
                                    onChange={(e) => updateChoice(index, e.target.value)}
                                    style={{
                                        borderColor: index === 0 ? "#52c41a" : undefined,
                                        backgroundColor: index === 0 ? "#f6ffed" : undefined,
                                    }}
                                />
                            </Col>
                            <Col span={4}>
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeChoice(index)}
                                    disabled={choices.length <= 2 || index === 0} // ไม่ให้ลบตัวเลือกแรก
                                    title={index === 0 ? "ไม่สามารถลบคำตอบที่ถูกต้องได้" : "ลบตัวเลือก"}
                                >
                                    ลบ
                                </Button>
                            </Col>
                        </Row>
                    ))}

                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={addChoice}
                        style={{ width: "100%", marginTop: 8 }}
                    >
                        เพิ่มตัวเลือก
                    </Button>

                    {/* <Divider>คำอธิบายคำตอบ</Divider> */}

                    <Form.Item
                        style={{ width: "100%", marginTop: 15 }}
                        name="explain_answer"
                        label="คำอธิบายคำตอบ"
                        // rules={[{ required: true, message: "กรุณากรอกคำอธิบายคำตอบ" }]}
                    >
                        <TextArea rows={4} placeholder="กรอกคำอธิบายคำตอบ" />
                    </Form.Item>

                    <Alert
                        message="คำแนะนำ"
                        description={
                            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                <li>ตัวเลือกแรก (มีเครื่องหมาย ✓) จะเป็นคำตอบที่ถูกต้องเสมอ</li>
                                <li>กรุณาใส่คำตอบที่ถูกต้องในช่องแรก</li>
                                <li>ตัวเลือกอื่นๆ จะเป็นคำตอบที่ผิด</li>
                                <li>ต้องมีตัวเลือกอย่างน้อย 2 ตัวเลือก</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                </Form>
            </Modal>
        </div>
    );
};

export default ExamManagement;
